import moment from 'moment';

const isoDateRegex = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d+)?)(?:Z|(\+|-)([\d|:]*))?$/;

export const parseIsoDate = (key: string, value: any) => {
  if (key && key.indexOf('Date') >= 0 && typeof value === 'string' && isoDateRegex.test(value)) {
    return new Date(value);
  } else {
    return value;
  }
};

export const getMomentDate = (value: any) => {
  if (typeof value === 'string' && isoDateRegex.test(value) || value instanceof Date) {
    const date = new Date(value);
    return moment(date);
  }
}

export const formatFileDate = (date: any) => {
  return moment(date).format('YYYY_MM_DD_HH_mm_ss');
}

export const formatDate = (value: any, format: string = 'DD/MM/YYYY') => {
  if (typeof value === 'string' && isoDateRegex.test(value) || value instanceof Date) {
    const date = new Date(value);
    return moment(date).format(format);
  } else {
    return value || '';
  }
}

export const formatDateFromNow = (value: any) => {
  if (typeof value === 'string' && isoDateRegex.test(value) || value instanceof Date) {
    const date = new Date(value);
    return moment(date).fromNow();
  } else {
    return value || '';
  }
};

export const truncate = (input: string, length: number) => input?.length > length ? `${input.substring(0, length)}...` : input;


export const parseJSON = (text: string | undefined, defVal?: any) => {
  try {
    if (text)
      return JSON.parse(text, parseIsoDate);
    else
      return defVal;
  } catch (err) {
    if (defVal) {
      // trackClient.error(`Error parsing JSON value: ${text}`, err);
      return defVal;
    }
    throw err;
  }
}

export const stringifyError = (err: any): string | undefined => {
  if (typeof err === 'string')
    return err;
  if (typeof err === 'object') {
    if (typeof err.error === 'object') {
      return stringifyError(err.error);
    } else if (typeof err.response === 'object' && typeof err.response.toJSON === 'function') {
      return stringifyError(err.response.toJSON()?.body);
    } else if (typeof err.body === 'string') {
      return stringifyError(parseJSON(err.body, err.body));
    } else {
      return err?.['odata.error']?.message?.value
        ?? err.error_description
        ?? err.error_message
        ?? err.message
        ?? err.error
        ?? JSON.stringify(err);
    }
  }
};

export function getObjectValues<T>(obj: { [key: string]: T }) {
  return obj ? Object.keys(obj).map(k => obj[k]) : [];
}

export function isConsentError(error: any) {
  return error.error === 'invalid_grant' ||
    error.error === 'insufficient_scope' ||
    error.errorCode === 'invalid_grant' ||
    error.errorCode === 'insufficient_scope' ||
    `${error.error?.code}`.indexOf('Authorization_RequestDenied') >= 0 ||
    `${error['odata.error']?.code}`.indexOf('ProjectServerUnauthorizedAccessException') >= 0;
}

export function unique<T>(array: T[], propertyName: keyof T) {
  return array.filter((e, i) => array.findIndex(a => a[propertyName] === e[propertyName]) === i);
}

export function pad(num: number, places: number) {
  return String(num).padStart(places, '0');
}

export function formatYMD(date?: Date) {
  const now = date || new Date();
  return `${pad(now.getFullYear(), 4)}${pad(now.getMonth() + 1, 2)}${pad(now.getDate(), 2)}`
}

export function traverseObject(obj: any, fn: (obj: any) => void) {
  fn(obj);

  for (const key in obj) {
    if (typeof obj[key] === 'object') {
      traverseObject(obj[key], fn);
    }
    if (Array.isArray(obj[key])) {
      for (const item of obj[key]) {
        traverseObject(item, fn);
      }
    }
  }
}

export const highlightText = (text: string | undefined, filters: string[]) => {
  if (text && filters.length > 0) {
    for (const filter of filters) {
      const regex = new RegExp(filter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
      text = text.replace(regex, `<span class='search-hilight'>$&</span>`);
    }
    return text;
  } else {
    return text || '';
  }
}

export const naturalComparer = Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

export const isEmailValid = (email: string) => {
  const validateEmailRegex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,10}$/g;
  return !!email.match(validateEmailRegex);
}

export function splitFilterWords(filter: string | undefined) {
  if (filter) {
    const filterLowerCase = filter.toLowerCase().trim();
    // Split filter into words, treating quoted phrases as one word
    const regex = /"([^"]+)"|'([^']+)'|\S+/g;
    const words = [];
    let match;
    while ((match = regex.exec(filterLowerCase)) !== null) {
      words.push((match[1] || match[2] || match[0]));
    }
    return words;
  } else {
    return [];
  }
}

export function trimString(value: string | undefined) {
  return value ? value.trim() : '';
}

export function isValidAcronym(acronym: string) {
  return /^[a-zA-Z0-9_]+$/.test(acronym)
}

export function makeAcronym(title: string) {
  return title
    .toUpperCase()
    .split(' ')
    .filter((x: string) => x.length >= 2 && x != 'THE')
    .map((word: string) => word.replace(/[()[\]{}<>""''`~!@#$%^&*+=|\\:;,./?\-_]/g, '')) // Remove punctuation and special characters explicitly
    .filter((word: string) => word.length > 0) // Filter out empty strings after character removal
    .map((word: string) => word[0])
    .join('');
}

export function isSameEmail(email1?: string, email2?: string): boolean {
  return (email1 || '').toLowerCase() === (email2 || '').toLowerCase();
}
